import React, { useState, useEffect } from 'react';
import { getGastos, createGasto, deleteGasto, getCategoriasGasto } from '../api';

const fmt = v => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
function fmtData(d) {
  const s = String(d).slice(0, 10).split('-');
  return s.length === 3 ? `${s[2]}/${s[1]}/${s[0]}` : '';
}

const GRUPOS_LABEL = {
  insumo: 'Insumo', mao_obra: 'Mão de obra', fixa: 'Fixa',
  variavel_custo: 'Variável', variavel_venda: 'Variável venda', investimento: 'Investimento',
};
const GRUPOS_COLOR = {
  insumo: 'var(--b)', mao_obra: 'var(--g)', fixa: 'var(--y)',
  variavel_custo: 'var(--c4)', variavel_venda: 'var(--r)', investimento: 'var(--c3)',
};
const ORIGEM_LABEL = { manual: null, compra_insumo: '🧺 via compra', conta_fixa: '📅 via conta fixa' };

function GastoForm({ categorias, onSubmit, onCancel, saving }) {
  const [descricao, setDescricao] = useState('');
  const [valor, setValor] = useState('');
  const [data, setData] = useState(new Date().toISOString().split('T')[0]);
  const [categoriaId, setCategoriaId] = useState('');
  const [forma, setForma] = useState('pix');

  const grupos = {};
  for (const c of categorias) {
    if (!grupos[c.grupo]) grupos[c.grupo] = [];
    grupos[c.grupo].push(c);
  }

  return (
    <form className="fm" onSubmit={e => {
      e.preventDefault();
      const cat = categorias.find(c => c.id === Number(categoriaId));
      onSubmit({
        descricao: descricao.trim(),
        valor: Number(valor),
        data,
        categoria_id: categoriaId ? Number(categoriaId) : undefined,
        categoria: cat?.nome || 'outros',
        forma_pagamento: forma,
      });
    }}>
      <input className="inp" placeholder="Descrição *" value={descricao} onChange={e => setDescricao(e.target.value)} required autoFocus />
      <div className="inp-r">
        <div style={{ flex: 1 }}>
          <input className="inp" type="number" step="0.01" min="0.01" placeholder="Valor R$ *" value={valor} onChange={e => setValor(e.target.value)} required />
        </div>
        <div style={{ flex: 1 }}>
          <input className="inp" type="date" value={data} onChange={e => setData(e.target.value)} />
        </div>
      </div>
      <select className="inp inp-sel" value={categoriaId} onChange={e => setCategoriaId(e.target.value)}>
        <option value="">Categoria (opcional)</option>
        {Object.entries(grupos).map(([grupo, cats]) => (
          <optgroup key={grupo} label={GRUPOS_LABEL[grupo] || grupo}>
            {cats.map(c => <option key={c.id} value={c.id}>{c.icone} {c.nome}</option>)}
          </optgroup>
        ))}
      </select>
      <select className="inp inp-sel" value={forma} onChange={e => setForma(e.target.value)}>
        <option value="pix">PIX</option>
        <option value="dinheiro">Dinheiro</option>
        <option value="cartao">Cartão</option>
        <option value="boleto">Boleto</option>
        <option value="">Não informado</option>
      </select>
      <div className="btn-g">
        <button className="btn btn1" type="submit" disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
        <button className="btn btn3" type="button" onClick={onCancel}>Cancelar</button>
      </div>
    </form>
  );
}

export default function GastosPage({ onToast }) {
  const [gastos, setGastos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [filtroGrupo, setFiltroGrupo] = useState('');

  const hoje = new Date();
  const [inicio, setInicio] = useState(`${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-01`);
  const [fim, setFim] = useState(hoje.toISOString().split('T')[0]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { inicio, fim };
      if (filtroGrupo) params.grupo = filtroGrupo;
      const [g, cats] = await Promise.all([getGastos(params), getCategoriasGasto()]);
      setGastos(g);
      setCategorias(cats.lista || []);
    } catch (err) { onToast(err.message, 'error'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [inicio, fim, filtroGrupo]);

  const doSave = async (fn, msg) => {
    setSaving(true);
    try { await fn(); onToast(msg); load(); }
    catch (err) { onToast(err.message, 'error'); }
    setSaving(false);
  };

  // Agrupamento por grupo para o resumo
  const porGrupo = {};
  for (const g of gastos) {
    const k = g.grupo || 'outros';
    porGrupo[k] = (porGrupo[k] || 0) + Number(g.valor);
  }
  const total = gastos.reduce((s, g) => s + Number(g.valor), 0);

  return (
    <div>
      {/* Filtros */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <input className="inp" type="date" value={inicio} onChange={e => setInicio(e.target.value)} style={{ maxWidth: 150 }} />
        <input className="inp" type="date" value={fim} onChange={e => setFim(e.target.value)} style={{ maxWidth: 150 }} />
        <select className="inp inp-sel" style={{ maxWidth: 160 }} value={filtroGrupo} onChange={e => setFiltroGrupo(e.target.value)}>
          <option value="">Todos os grupos</option>
          {Object.entries(GRUPOS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {/* Resumo por grupo */}
      {total > 0 && (
        <div className="c" style={{ padding: '12px 14px', marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Total: {fmt(total)}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {Object.entries(porGrupo).map(([k, v]) => (
              <span key={k} className="bd" style={{ background: `${GRUPOS_COLOR[k]}22`, color: GRUPOS_COLOR[k], border: `1px solid ${GRUPOS_COLOR[k]}55` }}>
                {GRUPOS_LABEL[k] || k}: {fmt(v)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="c-h" style={{ marginBottom: 12 }}>
        <span className="c-t">Gastos do período</span>
        <button className="btn btn1 btn-sm" onClick={() => setShowForm(v => !v)}>
          {showForm ? 'Fechar' : '+ Lançar gasto'}
        </button>
      </div>

      {showForm && (
        <div className="c" style={{ marginBottom: 12 }}>
          <GastoForm
            categorias={categorias}
            saving={saving}
            onSubmit={async data => {
              await doSave(() => createGasto(data), 'Gasto lançado!');
              setShowForm(false);
            }}
            onCancel={() => setShowForm(false)}
          />
        </div>
      )}

      {loading ? (
        <div style={{ padding: 24, color: 'var(--c4)' }}>Carregando...</div>
      ) : gastos.length === 0 ? (
        <div className="mt">
          <span className="mt-ico">🧾</span>
          Nenhum gasto no período. Compras de insumo aparecem aqui automaticamente.
        </div>
      ) : (
        <div className="lst">
          {gastos.map(g => (
            <div key={g.id} className="it">
              <div className="it-main">
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 14 }}>{g.icone} {g.descricao}</strong>
                  {g.grupo && (
                    <span className="bd" style={{ fontSize: 10, background: `${GRUPOS_COLOR[g.grupo]}22`, color: GRUPOS_COLOR[g.grupo], border: `1px solid ${GRUPOS_COLOR[g.grupo]}55` }}>
                      {GRUPOS_LABEL[g.grupo]}
                    </span>
                  )}
                  {g.categoria_nome && <span className="bd bd-r" style={{ fontSize: 10 }}>{g.categoria_nome}</span>}
                  {ORIGEM_LABEL[g.origem] && (
                    <span style={{ fontSize: 10, color: 'var(--c4)' }}>{ORIGEM_LABEL[g.origem]}</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: 'var(--c4)', marginTop: 2 }}>
                  {fmtData(g.data)}
                  {g.forma_pagamento && ` · ${g.forma_pagamento}`}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                <strong style={{ fontSize: 15, color: 'var(--r)' }}>{fmt(g.valor)}</strong>
                {g.origem === 'manual' && (
                  <button className="btn btn-sm" style={{ background: 'var(--r3)', color: 'var(--r)', border: '1px solid #fecaca' }}
                    onClick={() => doSave(() => deleteGasto(g.id), 'Gasto removido')}>✕</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
